import { assert } from "@faro-lotv/foundation";
import type { ICadModel } from "@faro-lotv/lotv";
import {
  BufferAttribute,
  BufferGeometry,
  Matrix4,
  Quaternion,
  Vector3,
  type WebGLRenderer,
} from "three";
import { GenerateMeshBVHWorker } from "three-mesh-bvh/src/workers/GenerateMeshBVHWorker.js";
import { CAD_BVH_DEPTH, CadDistanceReference } from "./cad-distance-reference";
import { obbToPlanes } from "@/hooks/use-clipping-planes";
import type { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";

const MAX_TRIANGLE_MEGABYTES = 192;
const MAX_TRIANGLE_BYTES = MAX_TRIANGLE_MEGABYTES * 1024 * 1024;
const MEMORY_MULTIPLIER = 4;
const MIN_TEXTURE_UNITS = 7;
const BUILD_TIMEOUT = 120_000;

/**
 * @param model CAD source with complete static/streamed geometry access.
 * @param objectIds Selected hierarchy IDs; undefined explicitly means the model root.
 * @param renderer Renderer capabilities, never mutated.
 * @param maxBytes Existing CAD memory policy.
 * @param signal Comparison cancellation.
 * @param onProgress Progress text and build stage for the panel.
 * @param worldMatrix Frozen source model transform.
 * @param clipBox Frozen world-space comparison region from the existing clip tool.
 * @returns Complete, owned CAD reference built off-thread, or a clear failure.
 */
export async function buildCadReference(
  model: Pick<
    ICadModel,
    "getAnalysisTriangles" | "boundingBox" | "matrixWorld"
  >,
  objectIds: number[] | undefined,
  renderer: {
    capabilities: Pick<
      WebGLRenderer["capabilities"],
      "maxVertexTextures" | "maxTextureSize"
    >;
  },
  maxBytes: number,
  signal: AbortSignal,
  onProgress: (message: string, stage: "geometry" | "bvh") => void,
  worldMatrix: Matrix4 = model.matrixWorld.clone(),
  clipBox?: OrientedBoundingBox,
): Promise<CadDistanceReference> {
  const controller = new AbortController();
  const timeout = setTimeout(
    () =>
      controller.abort(
        new Error("CAD comparison timed out. Select a smaller subtree."),
      ),
    BUILD_TIMEOUT,
  );
  const combined = AbortSignal.any([signal, controller.signal]);
  const geometry = new BufferGeometry();
  const origin = clipBox
    ? new Vector3().fromArray(clipBox.position)
    : model
        .boundingBox()
        .clone()
        .applyMatrix4(worldMatrix)
        .getCenter(new Vector3());
  const clippingPlanes = clipBox
    ? obbToPlanes({
        position: origin,
        quaternion: new Quaternion().fromArray(clipBox.rotation).normalize(),
        size: new Vector3().fromArray(clipBox.size),
      })
    : undefined;
  let worker: GenerateMeshBVHWorker | undefined;
  try {
    combined.throwIfAborted();
    assert(
      renderer.capabilities.maxVertexTextures >= MIN_TEXTURE_UNITS,
      "This graphics device cannot render CAD deviation colors",
    );
    const budget = Math.min(MAX_TRIANGLE_BYTES, maxBytes / MEMORY_MULTIPLIER);
    const batches: Float32Array[] = [];
    let length = 0;
    for await (const batch of model.getAnalysisTriangles({
      origin,
      worldMatrix,
      objectIds: objectIds ? new Set(objectIds) : undefined,
      maxBytes: budget,
      maxChunkBytes: maxBytes,
      clippingPlanes,
      signal: combined,
      onProgress: (done, total) =>
        onProgress(`Reading CAD geometry: ${done} / ${total}`, "geometry"),
    })) {
      batches.push(batch);
      length += batch.length;
    }
    assert(
      length > 0,
      clipBox
        ? "The selected CAD subtree has no triangular surfaces inside the clip box"
        : "The selected CAD subtree contains no triangular surfaces",
    );
    const vertices = length / 3;
    assert(
      Math.ceil(Math.sqrt(vertices)) <= renderer.capabilities.maxTextureSize,
      "The CAD reference exceeds this device's texture size. Select a smaller subtree.",
    );
    const positions = new Float32Array(length);
    let offset = 0;
    for (const batch of batches) {
      positions.set(batch, offset);
      offset += batch.length;
    }
    batches.length = 0;
    geometry.setAttribute("position", new BufferAttribute(positions, 3));
    combined.throwIfAborted();
    onProgress("Building CAD distance reference", "bvh");
    worker = new GenerateMeshBVHWorker();
    const activeWorker = worker;
    const bvh = await new Promise<
      Awaited<ReturnType<GenerateMeshBVHWorker["generate"]>>
    >((resolve, reject) => {
      function abort(): void {
        reject(combined.reason);
      }
      combined.addEventListener("abort", abort, { once: true });
      activeWorker
        .generate(geometry, { maxDepth: CAD_BVH_DEPTH, maxLeafTris: 8 })
        .then(resolve, reject)
        .finally(() => combined.removeEventListener("abort", abort));
      if (combined.aborted) abort();
    });
    combined.throwIfAborted();
    return new CadDistanceReference(bvh, origin, clipBox);
  } catch (error) {
    geometry.dispose();
    throw error;
  } finally {
    worker?.dispose();
    clearTimeout(timeout);
  }
}
